import type { Button } from '@coop/components';
import type { ComponentProps, ComponentPropsWithoutRef } from 'react';

import { Helpers } from '../../../../../utility/helpers';
import type {
    EpiButtonPropertyBlockType,
    EpiButtonSize,
    EpiButtonThemes,
} from './EpiButtonPropertyBlock.types';

type ButtonProps = ComponentPropsWithoutRef<typeof Button>;

export const mapEpiButtonPropertyBlock = (props: EpiButtonPropertyBlockType): ButtonProps => {
    return {
        theme: mapEpiButtonTheme(props.theme),
        disabled: props.disabled,
        size: mapEpiButtonSize(props.size),
        fullWidth: props.fullWidth,
        'aria-label': props.ariaLabel || undefined,
        style: {
            marginRight:
                props.alignment === 'start' || props.alignment === 'center' ? 'auto' : undefined,
            marginLeft:
                props.alignment === 'end' || props.alignment === 'center' ? 'auto' : undefined,
        },
        children: props.text,
    };
};

export const mapEpiButtonTheme = (
    theme?: EpiButtonThemes,
): ComponentProps<typeof Button>['theme'] => {
    switch (theme) {
        case 'Button--white':
        case 'Button--invertedGreen':
        case 'Button--whiteBlack':
            return 'white';
        case 'Button--greenDark':
        case 'Button--green':
            return 'primary';
        case 'Button--transparentWhite':
            return 'outlinedOnDark';
        case 'Button--transparentGreenDark':
        case 'Button--transparentGreen':
            return 'outlinedOnLight';

        case 'Button--light':
            return 'soft';
        case undefined:
            return 'primary';
        default:
            return exhaustiveGuard(theme);
    }
};

export const mapEpiButtonSize = (size: EpiButtonSize = '40'): ButtonProps['size'] => {
    switch (size) {
        case '40':
            return 'default';
        case '48':
            return 'large';
        case '56':
            return 'xlarge';
        default:
            return Helpers.exhaustiveCheck(size);
    }
};

const exhaustiveGuard = (_value: never): never => {
    throw new Error(
        `ERROR! Reached forbidden guard function with unexpected value: ${JSON.stringify(_value)}`,
    );
};
